import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { normalizeStreamType, type StationV2, type UrlV2 } from "@orvis/core";

/**
 * "What's on" for the radio player — the current song/show title, when the
 * stream publishes one:
 *
 *   - HLS radio carries ID3 timed metadata. Both hls.js and Safari's native
 *     player surface it as a `metadata` text track; we read TIT2/TPE1 (or a
 *     free-text TXXX) from its cues. No network.
 *   - Progressive MP3/AAC uses ICY metadata, which <audio> hides, so we ask
 *     the `nowPlaying` function (via the CDN-cached /api/now-playing
 *     Hosting rewrite) every 30 s while playing.
 */

const POLL_MS = 30_000;

function apiBase(): string {
  const env = import.meta.env.VITE_ORVIS_API_BASE as string | undefined;
  if (env) return env.replace(/\/$/, "");
  // Native bundles load from https://localhost — talk to the hosted site.
  return Capacitor.isNativePlatform() ? "https://orvisapp.web.app" : "";
}

function cueText(cue: TextTrackCue): { key?: string; info?: string; text?: string } {
  const v = (cue as unknown as { value?: { key?: string; data?: unknown; info?: string } }).value;
  if (v && typeof v.data === "string") return { key: v.key, info: v.info, text: v.data };
  const text = (cue as VTTCue).text;
  return typeof text === "string" ? { text } : {};
}

export function titleFromCues(cues: TextTrackCueList | null): string | undefined {
  if (!cues || cues.length === 0) return undefined;
  let song: string | undefined;
  let artist: string | undefined;
  let free: string | undefined;
  for (let i = 0; i < cues.length; i++) {
    const { key, info, text } = cueText(cues[i]!);
    const clean = text?.replace(/\0/g, "").trim();
    // Pure numbers are technical values (sample rate "48000", bitrates…).
    if (!clean || /^[\d\s.:,-]+$/.test(clean)) continue;
    if (key === "TIT2") song = clean;
    else if (key === "TPE1") artist = clean;
    // Free text only from untyped cues, or TXXX frames labelled as a title.
    // Encoders also emit TSSE ("encProSTREAM"), TXXX sample rates, PRIV
    // blobs and the like, none of which are "what's on".
    else if (!key || (key === "TXXX" && /title|song|now ?playing|artist/i.test(info ?? "")))
      free ??= clean.replace(/^StreamTitle=/i, "").replace(/^'|';?$/g, "");
  }
  if (song) return artist ? `${artist} - ${song}` : song;
  return free || undefined;
}

export function useNowPlayingTitle(
  country: string,
  station: StationV2 | undefined,
  activeUrl: UrlV2 | undefined,
  el: HTMLMediaElement | null,
  paused: boolean
): string | undefined {
  const [title, setTitle] = useState<string | undefined>();
  const isHls = activeUrl ? normalizeStreamType(activeUrl.type) === "HLS" : false;

  useEffect(() => setTitle(undefined), [station, activeUrl]);

  // HLS: ID3 metadata cues.
  useEffect(() => {
    if (!el || !isHls) return;
    const tracks = el.textTracks;
    const watched = new Set<TextTrack>();
    const onCue = (e: Event) => {
      const t = titleFromCues((e.target as TextTrack).activeCues);
      if (t) setTitle(t);
    };
    const watch = (track: TextTrack) => {
      if (track.kind !== "metadata" || watched.has(track)) return;
      track.mode = "hidden"; // cues only fire for non-disabled tracks
      track.addEventListener("cuechange", onCue);
      watched.add(track);
    };
    for (let i = 0; i < tracks.length; i++) watch(tracks[i]!);
    const onAdd = (e: TrackEvent) => e.track && watch(e.track as TextTrack);
    tracks.addEventListener("addtrack", onAdd);
    return () => {
      tracks.removeEventListener("addtrack", onAdd);
      watched.forEach((t) => t.removeEventListener("cuechange", onCue));
    };
  }, [el, isHls]);

  // Progressive: poll the ICY endpoint.
  useEffect(() => {
    if (!station?.id || !activeUrl || isHls || paused) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const url = `${apiBase()}/api/now-playing?cc=${encodeURIComponent(country)}&id=${encodeURIComponent(station.id)}`;
    const tick = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return; // 404: station not in the catalog — stop asking
        const body = (await res.json()) as { title?: string | null; supported?: boolean };
        if (stopped) return;
        setTitle(body.title ?? undefined);
        if (body.supported === false) return;
      } catch {
        /* offline / blocked — try again next tick */
      }
      if (!stopped) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [country, station, activeUrl, isHls, paused]);

  return title;
}
